import React, { useEffect, useRef, useState } from "react";
import { IS_DEV } from "../data/js/server-constants";
import { isOurPhotoUrl } from '../data/js/listing-photos';

const ASSET_SERVER = IS_DEV ? "" : "https://public.sellwild.com";

/** Variant formats the resize Lambda writes for every photo it processes. */
export type VariantFormat = "webp" | "avif";

/**
 * Whether to offer AVIF at all. A kill switch: set NEXT_PUBLIC_DISABLE_AVIF=1
 * to go back to webp-only everywhere without touching this file.
 */
export const AVIF_ENABLED = process.env.NEXT_PUBLIC_DISABLE_AVIF !== "1";

// A photo URL that is not ours gets handed back untouched — there is no variant
// to point at. Everything in our own storage gets the size and format transform,
// whichever feed the listing came from. The test is `isOurPhotoUrl`.
const Img = ({ src, ...rest }) => {
  return <img src={`${ASSET_SERVER}${src}`} {...rest} alt={""} />;
};

/** The square sizes the resize Lambda writes (lambdas/resize-images/index.mjs DEFAULT_SIZES). */
export const VARIANT_SIZES = [80, 160, 320, 640, 1280] as const;

/**
 * The variant to fetch for a requested size: the smallest one at least that
 * big, or the largest.
 *
 * Any other size is a URL that does not exist. It 404s twice (AVIF, then
 * WebP) and ResizedImg's fallback then loads the ORIGINAL full-size .jpg --
 * which is what My Listings did for every 72px thumbnail until 2026-10-09,
 * and the embed cards at 400 and 800.
 */
export function variantSize(size: number): number {
	return VARIANT_SIZES.find((v) => v >= size) ?? VARIANT_SIZES[VARIANT_SIZES.length - 1];
}

export const newImage = (photo, requestedSize, _dataSourceId?, format: VariantFormat = "webp") => {
	if (!photo) return "";
	const size = variantSize(Number(requestedSize));

	// An existing suffix may be either format: a caller can hand us a URL this
	// function already produced.
	const existingSuffix = /_\d+x\d+\.(?:webp|avif)$/;

	// Anything in our own storage gets the size/format transform, whatever feed
	// the listing came from. `dataSourceId` is no longer consulted -- see
	// `isOurPhotoUrl` for why the rule moved to the URL.
	//
	// It also has to be OUR bucket, not merely an S3 one: this used to test
	// `photo.includes(".s3.")`, which matches a supplier's own bucket
	// (gccvehiclemedia.s3.amazonaws.com) just as happily and would rewrite a
	// foreign photo into a 404.
	if (isOurPhotoUrl(photo)) {
		// Lambda generates _<size>x<size>.webp and _<size>x<size>.avif for each
		// of 1280, 640, 320, 160, 80.
		if (existingSuffix.test(photo)) {
			// Replace existing size suffix with requested size
			return photo.replace(existingSuffix, `_${size}x${size}.${format}`);
		}

		// Legacy: No size suffix, add one
		const ps = photo.split(".");
		const ps2 = ps.slice(0, ps.length - 1);
		const front = ps2.join(".");
		return `${front}_${size}x${size}.${format}`;
	}

	// Foreign-hosted (a feed not yet mirrored) - return as-is
	if (!photo.includes("sellwild.com")) return photo;

	// Legacy logic for old URLs
	const qs = photo.split("?");
	const as = qs[0].split("/");
	const folder = as[as.length - 2];
	// Above has been resized.  Non-sellwild photos are on their own.
	if (folder < 147) return photo;
	const ps = qs[0].split(".");
	const ps2 = ps.slice(0, ps.length - 1);
	const front = ps2.join(".");

	return `${front}_${size}x${size}.${format}`;
};

export default Img;

export const ResizedImg = ({
	src,
	size,
	retinaSize,
	alt = "",
	className = "",
	fade: _fade = false,
	dataSourceId,
	style = {},
	onError: externalOnError,
	loading,
}: {
	src: string;
	/** CSS size the image renders at — this is what a 1x screen downloads. */
	size: number;
	/**
	 * Larger variant to offer 2x-density screens, via srcSet. The browser picks;
	 * a 1x screen never downloads it. Omit to serve one size to everyone.
	 *
	 * Skipped automatically when both sizes resolve to the same URL, which is
	 * the case for foreign-hosted photos — they have no variants to choose from.
	 */
	retinaSize?: number;
	alt?: string;
	className?: string;
	fade?: boolean;
	dataSourceId?: number;
	style?: React.CSSProperties;
	onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
	/**
	 * "lazy" for tiles in long feeds. Without it every card in every showcase
	 * row downloads and decodes at once, including the ten or so a row holds
	 * off to the right of the screen. Left unset for hero and product photos,
	 * which are the page's largest paint.
	 */
	loading?: "lazy" | "eager";
}) => {
	const newSrc = newImage(src, size, dataSourceId);
	const retinaSrc = retinaSize ? newImage(src, retinaSize, dataSourceId) : undefined;
	const srcSet = retinaSrc && retinaSrc !== newSrc
		? `${newSrc} 1x, ${retinaSrc} 2x`
		: undefined;
	const erroredRef = useRef(0);
	const ref = useRef(null);

	// AVIF is offered through a <source>, which the browser picks purely on
	// format support -- it does NOT fall through to the next candidate when the
	// file 404s. Not every photo has an AVIF sibling yet (only those uploaded
	// since the resizer started writing them, plus whatever has been backfilled),
	// so a miss is caught by onError below and retried without the AVIF source.
	//
	// Stored as the src it failed for, rather than a boolean reset in an effect:
	// a new src is then already un-failed on the first render, with no extra
	// pass and no window where the withdrawn source applies to the wrong photo.
	const [avifFailedFor, setAvifFailedFor] = useState<string | null>(null);
	const avifFailed = avifFailedFor === src;

	const avifSrc = AVIF_ENABLED ? newImage(src, size, dataSourceId, "avif") : newSrc;
	// newImage returns foreign-hosted photos untouched, so an unchanged URL means
	// there is no variant to offer and the <source> must be omitted entirely --
	// otherwise we would advertise a JPEG as image/avif.
	const hasAvif = AVIF_ENABLED && !avifFailed && avifSrc !== newSrc;

	const avifRetinaSrc = hasAvif && retinaSize
		? newImage(src, retinaSize, dataSourceId, "avif")
		: undefined;
	const avifSrcSet = avifRetinaSrc && avifRetinaSrc !== avifSrc
		? `${avifSrc} 1x, ${avifRetinaSrc} 2x`
		: avifSrc;

	// Reset error counter when src changes. erroredRef is only read in the
	// onError handler, which fires after commit, so resetting in an effect
	// (rather than during render) preserves behavior.
	useEffect(() => {
		erroredRef.current = 0;
	}, [src]);

	// Fallback strategy:
	// 0. AVIF missing: drop the <source> and let the browser re-pick webp.
	// 1. First error: Try replacing .webp with .jpg (for older listings, also strip size suffix)
	// 2. Second error: Try original src
	// 3. Give up after 3 attempts
	//
	// Returns true when the failure was handled internally and the outside world
	// should NOT be told the photo is broken.
	const handleFailure = (target: HTMLImageElement): boolean => {
		// A photo with no AVIF sibling fails here first. Re-rendering without the
		// AVIF <source> puts the browser back on the webp it would have chosen
		// anyway, so webp still shows even when AVIF is absent. Deliberately does
		// NOT consume an erroredRef attempt: the webp has not been tried yet, and
		// spending one here would cost the .jpg its turn further down.
		//
		// It also must not reach `externalOnError`: callers treat that as "this
		// photo is broken, stop showing it" (ListingRow files the index in a
		// `brokenImages` set), and a missing AVIF is a format we chose to offer,
		// not a missing photo. The webp is about to render.
		if (hasAvif) {
			setAvifFailedFor(src);
			return true;
		}

		const attempt = erroredRef.current;
		if (attempt >= 3) return false;
		erroredRef.current = attempt + 1;

		// srcSet outranks src, so a fallback assigned to src alone would be
		// ignored while the failing candidate stayed selected.
		if (target.srcset) target.srcset = '';
		if (attempt === 0 && target.src.endsWith('.webp')) {
			// Try JPG version without size suffix (old images don't have _1280x1280 variants)
			target.src = target.src.replace(/_\d+x\d+\.webp$/, '.jpg');
		} else {
			// Fall back to original src
			target.src = src;
		}
		return false;
	};

	const onError = (e: React.SyntheticEvent<HTMLImageElement>) => {
		const recovered = handleFailure(e.target as HTMLImageElement);
		if (!recovered && externalOnError) {
			externalOnError(e);
		}
	};

	// Statically-generated pages finish loading -- and failing -- their images
	// long before the React bundle hydrates, and React does NOT replay an error
	// that fired before its listener existed. `onError` above therefore never
	// runs for an above-the-fold photo whose AVIF 404s on a cold load, and the
	// broken image stays broken for the life of the page.
	//
	// A decoded image always reports naturalWidth > 0, so complete-with-zero-width
	// at attach time is exactly that missed failure. Runs on every mount, which
	// includes the remount after the AVIF source is withdrawn, so a webp that is
	// already-failed-and-cached advances to .jpg on the same path.
	const attachImg = (node: HTMLImageElement | null) => {
		ref.current = node;
		if (!node || !newSrc) return;
		if (node.complete && node.naturalWidth === 0) {
			const recovered = handleFailure(node);
			if (!recovered && externalOnError) {
				externalOnError({ target: node, currentTarget: node } as unknown as React.SyntheticEvent<HTMLImageElement>);
			}
		}
	};

	// const fadeIn = (t) => {
	// 	t.style.opacity = 1;
	// };

	// const onLoad = (e) => {
	// 	fadeIn(e.target);
	// };

	// useEffect(() => {
	// 	if (ref?.current?.complete) {
	// 		fadeIn(ref.current);
	// 	}
	// }, [ref?.current]);

	const img = (
		<img
			src={newSrc}
			srcSet={srcSet}
			ref={attachImg}
			// Remounts when the AVIF source is withdrawn, so the browser redoes
			// source selection instead of holding the candidate that just 404'd.
			key={`${src}:${hasAvif ? "avif" : "webp"}`}
			alt={alt}
			className={className}
			onError={onError}
			loading={loading}
			decoding={loading === "lazy" ? "async" : undefined}
			style={style}
			// onLoad={onLoad}
			// style={{ opacity: fade ? 0 : 1 }}
		/>
	);

	// No <picture> wrapper unless there is genuinely a second format to offer:
	// it would change layout for every foreign-hosted photo for nothing.
	if (!hasAvif) return img;

	return (
		<picture>
			<source type="image/avif" srcSet={avifSrcSet} />
			{img}
		</picture>
	);
};
